import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import {
  Compass,
  Loader,
  Maximize2,
  Minimize2,
  Search,
  ScanSearch,
  X,
} from 'lucide-react';
import { ATLAS_DEFAULT_CLUSTER } from '../../../utils/atlasItemIcon';
import { displayQuestTitle, type QuestChapterModel } from '../../../utils/questSnbt/model';
import { cn } from '../../../utils/cn';
import { QuestChapterCanvas, type QuestChapterCanvasHandle } from './QuestChapterCanvas';
import { QuestDetailPanel } from './QuestDetailPanel';

export interface QuestViewerWindowProps {
  title: string;
  filename: string;
  model: QuestChapterModel;
  clusterId?: string;
  clusterOptions?: string[];
  onClose: () => void;
}

const DEFAULT_SIZE = { w: 1100, h: 720 };

/**
 * Floating in-app window (not a new browser tab) for the chapter map.
 * Read-only — selection / camera state is local so a future editor can
 * reuse the same canvas without rewriting interaction.
 */
export const QuestViewerWindow = ({
  title,
  filename,
  model,
  clusterId: initialCluster,
  clusterOptions = [],
  onClose,
}: QuestViewerWindowProps) => {
  const canvasRef = useRef<QuestChapterCanvasHandle>(null);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [maximized, setMaximized] = useState(true);
  const [pos, setPos] = useState({ x: 48, y: 48 });
  const [size, setSize] = useState(DEFAULT_SIZE);
  const [clusterId, setClusterId] = useState(initialCluster || clusterOptions[0] || ATLAS_DEFAULT_CLUSTER);
  const dragRef = useRef<{ dx: number; dy: number } | null>(null);

  const clusters = useMemo(() => {
    const set = new Set<string>([ATLAS_DEFAULT_CLUSTER, 'common', ...clusterOptions]);
    if (initialCluster) set.add(initialCluster);
    return Array.from(set).filter(Boolean);
  }, [clusterOptions, initialCluster]);

  useEffect(() => {
    setSelectedId(null);
    setSearch('');
  }, [model.id, filename]);

  const selected = selectedId ? model.questById.get(selectedId) ?? null : null;

  const matchCount = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return 0;
    return model.quests.filter(
      (quest) => displayQuestTitle(quest.title).toLowerCase().includes(q) || quest.id.toLowerCase().includes(q),
    ).length;
  }, [model.quests, search]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        if (selectedId) setSelectedId(null);
        else onClose();
      }
      if (e.key === 'f' && (e.metaKey || e.ctrlKey)) {
        e.preventDefault();
        canvasRef.current?.fit();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose, selectedId]);

  const onTitlePointerDown = (e: React.PointerEvent) => {
    if (maximized) return;
    if ((e.target as HTMLElement).closest('button, input, select')) return;
    dragRef.current = { dx: e.clientX - pos.x, dy: e.clientY - pos.y };
    (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);
  };

  const onTitlePointerMove = (e: React.PointerEvent) => {
    if (!dragRef.current) return;
    setPos({
      x: Math.max(0, e.clientX - dragRef.current.dx),
      y: Math.max(0, e.clientY - dragRef.current.dy),
    });
  };

  const onTitlePointerUp = () => {
    dragRef.current = null;
  };

  const focusFirstMatch = useCallback(() => {
    const q = search.trim().toLowerCase();
    if (!q) return;
    const found = model.quests.find(
      (quest) => displayQuestTitle(quest.title).toLowerCase().includes(q) || quest.id.toLowerCase().includes(q),
    );
    if (!found) return;
    setSelectedId(found.id);
    canvasRef.current?.focusQuest(found.id);
  }, [model.quests, search]);

  const frameStyle: React.CSSProperties = maximized
    ? { inset: 16, width: 'auto', height: 'auto' }
    : { left: pos.x, top: pos.y, width: size.w, height: size.h };

  return createPortal(
    <div className="fixed inset-0 z-[80]">
      <div className="absolute inset-0 bg-black/55" onClick={onClose} />
      <div
        className="absolute glass-card overflow-hidden flex flex-col shadow-2xl shadow-black/50 border border-glass-200/20"
        style={frameStyle}
        onClick={(e) => e.stopPropagation()}
      >
        <div
          className={cn(
            'flex items-center gap-2 px-3 py-2 border-b border-glass-200/15 bg-glass-100/5 flex-shrink-0',
            !maximized && 'cursor-grab',
          )}
          onPointerDown={onTitlePointerDown}
          onPointerMove={onTitlePointerMove}
          onPointerUp={onTitlePointerUp}
        >
          <Compass className="w-4 h-4 text-amber-300 flex-shrink-0" />
          <div className="min-w-0 flex-1">
            <p className="text-sm font-semibold text-white truncate">{title}</p>
            <p className="text-[10px] text-glass-500 truncate">
              {filename} · {model.quests.length} quests · {model.iconItemIds.length} icons
            </p>
          </div>
          <div className="hidden md:flex items-center gap-1.5 flex-shrink-0">
            <div className="relative">
              <Search className="w-3.5 h-3.5 text-glass-500 absolute left-2 top-1/2 -translate-y-1/2" />
              <input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') focusFirstMatch();
                }}
                placeholder="Find quest…"
                className="glass-input text-xs py-1 pl-7 pr-2 w-44"
              />
            </div>
            {search.trim() && (
              <span className="text-[10px] text-glass-500 w-8">{matchCount}</span>
            )}
            <select
              value={clusterId}
              onChange={(e) => setClusterId(e.target.value)}
              className="glass-input text-xs py-1 px-2 max-w-[140px]"
              title="Atlas cluster for item icons"
            >
              {clusters.map((c) => (
                <option key={c} value={c}>
                  {c}
                </option>
              ))}
            </select>
            <button
              type="button"
              onClick={() => canvasRef.current?.fit()}
              className="glass-button-secondary text-xs py-1 px-2 inline-flex items-center gap-1"
              title="Fit chapter (Ctrl/Cmd+F)"
            >
              <ScanSearch className="w-3.5 h-3.5" />
              Fit
            </button>
          </div>
          <button
            type="button"
            onClick={() => setMaximized((v) => !v)}
            className="p-1.5 rounded text-glass-400 hover:text-white"
            title={maximized ? 'Restore' : 'Maximize'}
          >
            {maximized ? <Minimize2 className="w-4 h-4" /> : <Maximize2 className="w-4 h-4" />}
          </button>
          <button type="button" onClick={onClose} className="p-1.5 rounded text-glass-400 hover:text-white" title="Close">
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="md:hidden flex items-center gap-2 px-3 py-2 border-b border-glass-200/10">
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') focusFirstMatch();
            }}
            placeholder="Find quest…"
            className="glass-input text-xs py-1 px-2 flex-1"
          />
          <button type="button" onClick={() => canvasRef.current?.fit()} className="glass-button-secondary text-xs py-1 px-2">
            Fit
          </button>
        </div>

        <div className="flex-1 min-h-0 flex flex-col md:flex-row">
          <div className="flex-1 min-h-[240px] md:min-h-0 relative">
            <QuestChapterCanvas
              ref={canvasRef}
              model={model}
              clusterId={clusterId}
              selectedId={selectedId}
              onSelect={setSelectedId}
              searchQuery={search}
            />
            <div className="pointer-events-none absolute left-3 bottom-3 text-[10px] text-glass-500 bg-black/40 rounded px-2 py-1">
              Drag to pan · scroll to zoom · click a quest
            </div>
          </div>
          <aside className="md:w-80 flex-shrink-0 border-t md:border-t-0 md:border-l border-glass-200/15 bg-glass-100/5 max-h-[40vh] md:max-h-none">
            <QuestDetailPanel quest={selected} clusterId={clusterId} chapterTitle={title} />
          </aside>
        </div>

        {!maximized && (
          <div
            className="absolute bottom-0 right-0 w-4 h-4 cursor-se-resize"
            onPointerDown={(e) => {
              e.preventDefault();
              const start = { x: e.clientX, y: e.clientY, w: size.w, h: size.h };
              const move = (ev: PointerEvent) => {
                setSize({
                  w: Math.max(640, start.w + ev.clientX - start.x),
                  h: Math.max(420, start.h + ev.clientY - start.y),
                });
              };
              const up = () => {
                window.removeEventListener('pointermove', move);
                window.removeEventListener('pointerup', up);
              };
              window.addEventListener('pointermove', move);
              window.addEventListener('pointerup', up);
            }}
          />
        )}
      </div>
    </div>,
    document.body,
  );
};

export function QuestViewerLoadingOverlay({ onClose, label }: { onClose: () => void; label?: string }) {
  return createPortal(
    <div className="fixed inset-0 z-[80] flex items-center justify-center">
      <div className="absolute inset-0 bg-black/55" onClick={onClose} />
      <div className="relative glass-card px-6 py-5 flex items-center gap-3">
        <Loader className="w-5 h-5 animate-spin text-accent-400" />
        <span className="text-sm text-glass-200">{label || 'Opening chapter…'}</span>
      </div>
    </div>,
    document.body,
  );
}
